import React, { useContext, useEffect, useMemo, useState } from 'react';
import { DataContext } from '../components/DataContext';
import TableViewer from './TableViewer';
import '../styles/Employees.css';

const extractStartDate = (summary) => {
    const match = summary?.match(/\[(\d{1,2}), (\d{1,2}), (\d{4})\]/);
    return match ? new Date(Number(match[3]), Number(match[1]) - 1, Number(match[2])) : new Date(0);
};

const columnFormatDict = {
    employee: { name: 'Employee', pos: 1, width: 2 },
    est_comm_per_hour: { name: 'Est. Comm / Hour', type: 'currency', pos: 10, just: 'right' },
    est_commission: { name: 'Est. Commission', type: 'currency', pos: 11, just: 'left' },
    sales_comm: { name: 'Sales Comm', type: 'currency', pos: 4 },
    sales_per_hour: { name: 'Sales / Hour', type: 'percentage', pos: 5 },
    sales_bonus: { name: 'Sales Bonus', type: 'currency', pos: 6 },
    repair_comm: { name: 'Repair Comm', type: 'currency', pos: 7 },
    repairs_per_hour: { name: 'Repairs / Hour', type: 'percentage', pos: 8 },
    repair_bonus: { name: 'Repair Bonus', type: 'currency', skip: true },
    hours: { name: 'Hours', type: 'float', pos: 30 },
    gp_per_hour: { name: 'GP / Hour', type: 'currency', pos: 2, just: 'right' },
    total_gp: { name: 'Total GP', type: 'currency', pos: 3, just: 'right' },
    sales: { name: 'Sales', type: 'int', pos: 13, just: 'center' },
    repairs: { name: 'Repairs', type: 'int', pos: 14, just: 'center' },
    cogs: { name: 'COGS', type: 'currency', pos: 15, just: 'right', skip: true },
    cogs_percent: { name: 'COGS %', type: 'percentage', pos: 16, just: 'left', skip: true },
    discounts: { name: 'Discounts', type: 'currency', pos: 17, just: 'right', skip: true },
    discount_percent: { name: 'Discount %', type: 'percentage', pos: 18, just: 'left', skip: true },
    phone_repairs: { name: 'Phone (R)', type: 'int', pos: 19, just: 'left', skip: true },
    tablet_repairs: { name: 'Tablet (R)', type: 'int', pos: 20, just: 'left', skip: true },
    solder_repairs: { name: 'Solder (R)', type: 'int', pos: 21, just: 'left', skip: true },
    other_repairs: { name: 'Other (R)', type: 'int', pos: 22, just: 'left', skip: true },
    cpo_sales: { name: 'CPO (S)', type: 'int', pos: 23, just: 'left' },
    protection_sales: { name: 'Protection (S)', type: 'int', pos: 24, just: 'left' },
    accessories_sales: { name: 'Accessories (S)', type: 'int', pos: 25, just: 'left' },
    quotes: { name: 'Quotes', type: 'int', pos: 26, just: 'left' },
    total_sales: { name: 'Total Sales', type: 'currency', pos: 12, just: 'left', skip: true },
    week_summary: { skip: true },
};

const Employees = () => {
    const {
        employeeKpis,
        loadingEmployee,
        fetchingHistorical,
        historicalLoaded,
        fetchHistorical,
    } = useContext(DataContext);
    const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(0);

    const periods = useMemo(() => [...new Set(employeeKpis.map((item) => item.week_summary).filter(Boolean))]
        .sort((a, b) => extractStartDate(b) - extractStartDate(a)), [employeeKpis]);

    useEffect(() => {
        setSelectedPeriodIndex((current) => Math.min(current, Math.max(periods.length - 1, 0)));
    }, [periods.length]);

    const handlePeriodChange = async (direction) => {
        const nextIndex = selectedPeriodIndex + direction;
        if (nextIndex >= 0 && nextIndex < periods.length) {
            setSelectedPeriodIndex(nextIndex);
            return;
        }

        if (direction > 0 && !historicalLoaded) {
            const previousLength = periods.length;
            try {
                await fetchHistorical();
                setSelectedPeriodIndex(previousLength);
            } catch {
                // DataContext exposes the error state; keep the current period selected.
            }
        }
    };

    if (loadingEmployee) return <p>Loading employee data…</p>;

    return (
        <div className="employees">
            {fetchingHistorical && <div className="app-status historical-loading" role="status">Fetching historical data…</div>}
            <header className="dashboard-header"><h1>Employee KPIs</h1></header>
            <div className="dashboard-content">
                <div className="button-container">
                    <div className="period">Current {periods[selectedPeriodIndex] || 'No period available'}</div>
                    <button type="button" onClick={() => handlePeriodChange(-1)} disabled={selectedPeriodIndex <= 0 || fetchingHistorical}>⬅️</button>
                    <button
                        type="button"
                        onClick={() => handlePeriodChange(1)}
                        disabled={fetchingHistorical || (historicalLoaded && selectedPeriodIndex >= periods.length - 1)}
                        aria-label={selectedPeriodIndex >= periods.length - 1 && !historicalLoaded ? 'Load older historical data' : 'Previous period'}
                    >➡️</button>
                </div>
                <TableViewer dataKey={employeeKpis} colFormatDict={columnFormatDict} timestamp={periods[selectedPeriodIndex]} />
            </div>
        </div>
    );
};

export default Employees;
